import type { ReactNode } from 'react'
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import type { DefaultValues, FieldValues } from 'react-hook-form'
import type { Resolver } from 'react-hook-form/dist/types/resolvers'

import PlusIcon from '@/assets/icons/icon-plus.svg'
import { Button } from '@/components/ui/button.tsx'
import type {
  Entry,
  FolderEntry,
} from '@/components/ui/table/expandable-dnd-table/useTreeTable'
import { useToast } from '@/components/ui/toast.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { openDialog } from '@/models/dialogs.ts'
import { SettingsOption } from '@/modules/creator-settings/ui/SettingsOption'
import type { MediaOption } from '@/modules/message/components/editor/fields/message-media-dropdown'
import { MessageEditorProvider } from '@/modules/message/components/editor/message-editor-context'
import { MessageEditorDialog } from '@/modules/message/components/editor/message-editor-dialog'
import type {
  DefaultMessageEditorValues,
  MessageEditorComponent,
} from '@/modules/message/components/editor/schema'
import {
  DefaultSchemaBase,
  defaultComponents,
} from '@/modules/message/components/editor/schema'
import type { MessageBase } from '@/modules/message/components/sequence/message-sequence-editor-table'
import {
  MessageSequenceEditorTable,
  listColumns,
} from '@/modules/message/components/sequence/message-sequence-editor-table'
import type { MessageContextType } from '@/modules/message/components/sequence/types.ts'
import { useMessageMediaQuery } from '@/modules/message/query/message-media-query-context'
import { handleError } from '@/utils/error'
import { classNames } from '@/utils/helpers'

export interface GeneratePreviewIndicators<T> {
  (message: T): {
    rows: { label: string; value: string }[]
    title: string
  }
}
type MessagesTableProps<T extends MessageBase> = ComponentProps<'div'> & {
  title: ReactNode
  description: ReactNode
  placeholder?: string
  dialogTitle: ReactNode
  dialogDescription: ReactNode
  entries: T[]
  components?: readonly MessageEditorComponent[]
  submitError?: unknown
  isSubmitting?: boolean
  ordered?: boolean
  schema?: Resolver<T>
  defaultValues?: DefaultValues<T>
  onCreate?: (message: T) => Promise<unknown> | void
  onUpdate?: (message: T) => Promise<unknown> | void
  onRemove?: (message: T) => Promise<unknown> | void
  isRemoving?: boolean
  disabled?: boolean
  actionsDropDown?: boolean
  sortOptions?: ReactNode
  generatePreviewIndicators?: GeneratePreviewIndicators<T>
  addMessageLabel?: ReactNode
  type: MessageContextType
}

function toTree<T extends FieldValues>(
  message: T,
  order: number,
): FolderEntry<T> {
  return {
    ...message,
    _type: 'folder',
    _id: () => message.id,
    _order: () => order,
    _withOrder: (newOrder: number) => toTree(message, newOrder),
    _listChildren: () => [],
    _withChildren: () => toTree(message, order),
  }
}

const defaultResolver = zodResolver(DefaultSchemaBase)

const baseDefaultValues = {
  id: null,
  text: '',
  fallback: '',
  fallbackVariable: '',
  medias: [],
  price: 0,
  enableFallbackVariable: false,
}

function mapDataToReplaceTextWithMedia<T extends MessageBase>(
  element: T,
  mediaOptions: MediaOption[],
) {
  const medias: MediaOption[] = element.medias.map(
    ({ mediaId }: { mediaId: number }) => {
      return mediaOptions.find((media) => media.mediaId === mediaId)
    },
  )

  const mediaNames = medias.map((media) => media?.name).join(', ')

  return {
    ...element,
    fallbackText: mediaNames,
  }
}

export const MessageSequenceEditor = <T extends MessageBase>(
  props: MessagesTableProps<T>,
) => {
  const {
    title,
    description,
    placeholder,
    dialogTitle,
    components,
    schema,
    dialogDescription,
    submitError,
    entries,
    className,
    onCreate,
    onUpdate,
    onRemove,
    defaultValues,
    ordered,
    isSubmitting,
    isRemoving,
    disabled,
    addMessageLabel,
    type,
    actionsDropDown,
    sortOptions,
    generatePreviewIndicators,
    ...rest
  } = props

  const toast = useToast()

  const [data, setData] = useState<Entry<T>[]>([])

  useEffect(() => {
    setData(
      entries
        .sort((left, right) => (left?.order ?? 0) - (right?.order ?? 0))
        .map((it) => toTree(it, it.id ?? 0)),
    )
  }, [entries])

  const [isEditorOpen, setShowEditor] = useState(false)
  const handleCloseEditor = useCallback(() => {
    setShowEditor(false)
  }, [])

  const [edited, setEdited] = useState<T>()

  const handleOpenEditor = useCallback(() => {
    setEdited(undefined)
    setShowEditor(true)
  }, [setEdited, setShowEditor])

  const handleSubmit = useCallback(
    async (message: T) => {
      if (message.id !== null && message.id !== undefined) {
        try {
          await onUpdate?.(message)
          if (onUpdate) {
            toast.success('Message was updated')
          }
          setShowEditor(false)

          return
        } catch (error) {
          handleError(error, 'Failed to update message')

          return
        }
      }
      try {
        await onCreate?.(message)
        toast.success('Message created successfully')
        setShowEditor(false)
      } catch (error) {
        handleError(error, 'Failed to create message')
      }
    },
    [onCreate, onUpdate, toast],
  )

  const columns = useMemo(
    () =>
      listColumns<T>({
        ordered: false, // ordered,
        disabled,
        actionsDropDown,
        generatePreviewIndicators,
        onEdit: (item) => {
          setEdited(item)
          setShowEditor(true)
        },
        onRemove: (item) => {
          if (item?.id === undefined || item?.id === null || !onRemove) {
            return
          }

          openDialog({
            type: DIALOG_TYPE.REMOVE_MESSAGE_CONFIRMATION,
            onDelete: async () => {
              try {
                await onRemove?.(item)
                toast.success('The message was removed')
              } catch (error) {
                handleError(error, 'Failed to remove the message')
                throw error
              }
            },
          })
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      actionsDropDown,
      disabled,
      generatePreviewIndicators,
      isRemoving,
      onRemove,
      toast,
    ],
  )

  const medias = useMessageMediaQuery()

  const mediaOptions = useMemo<MediaOption[]>(
    () =>
      medias.orElse([]).map(({ id, mediaAttributes }) => ({
        mediaId: id,
        name: mediaAttributes?.label ?? '-',
        sorting: 0,
      })),
    [medias],
  )

  const tableData = useMemo(
    () =>
      data.map((element) => {
        if (element.text && element.text !== '') {
          return element
        }

        if (!element.medias) {
          return element
        }

        const remappedElement = mapDataToReplaceTextWithMedia(
          element,
          mediaOptions,
        )

        return remappedElement
      }),
    [data, mediaOptions],
  )

  // Compute the default values from the active edit model and fallbacks.
  const initialFormState = useMemo<
    DefaultMessageEditorValues<MessageBase>
  >(() => {
    return {
      ...baseDefaultValues,
      ...defaultValues,
      ...edited,
    } as DefaultMessageEditorValues<MessageBase>
  }, [defaultValues, edited])

  return (
    <div
      {...rest}
      className={classNames('grid', className)}
    >
      <SettingsOption
        title={title}
        description={description}
        biggerTitle
        mobileVertical
      >
        <div className="flex flex-wrap gap-2 md:flex-nowrap">
          {sortOptions}
          <Button
            className="min-w-36"
            onClick={handleOpenEditor}
            disabled={disabled}
          >
            <PlusIcon />
            {addMessageLabel ?? 'Add Message'}
          </Button>
        </div>
      </SettingsOption>
      <div>
        <MessageSequenceEditorTable
          data={tableData}
          onDataChange={setData}
          disableDrag={true /*!ordered*/}
          columns={columns}
        />
        <MessageEditorProvider
          textPlaceholder={placeholder}
          defaultValues={initialFormState}
          components={components ?? defaultComponents}
          onSubmit={handleSubmit}
          schema={schema ?? defaultResolver}
          type={type}
        >
          <MessageEditorDialog
            title={dialogTitle}
            description={dialogDescription}
            onClose={handleCloseEditor}
            isOpen={isEditorOpen}
            isEditing={!!edited}
            submitError={submitError}
            isSubmitting={isSubmitting}
          />
        </MessageEditorProvider>
      </div>
    </div>
  )
}
